import React, { useState } from 'react';
import { useStore } from '../../store/StoreContext';
import { Input, Select } from '../../components/UI/Input';
import { Button } from '../../components/UI/Button';
import { ImageUpload } from '../../components/UI/ImageUpload';
import { generateId, getPriorityColor, formatDuration } from '../../utils/helpers';
import SafeIcon from '../../common/SafeIcon';
import { CategoryModal } from './CategoryModal';

export const Step1Tasks = () => {
  const { state, addEntity, updateEntity, deleteEntity } = useStore();
  const [activeTab, setActiveTab] = useState('all');
  const [editorTab, setEditorTab] = useState('general');
  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [newUrl, setNewUrl] = useState('');
  
  const [taskForm, setTaskForm] = useState({
    name: '',
    description: '',
    notes: '',
    assets: [],
    hours: 0,
    minutes: 0,
    priority: 'medium',
    tags: '',
    categoryId: '',
    isAppTask: false,
    appUrl: '',
    logoUrl: ''
  });

  const taskCategories = Object.values(state.categories || {}).filter(c => c.type === 'task');
  const tasks = Object.values(state.taskTemplates || {});
  const filteredTasks = tasks.filter(t => activeTab === 'all' || t.categoryId === activeTab);

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    const durationMinutes = (parseInt(taskForm.hours || 0) * 60) + parseInt(taskForm.minutes || 0);
    const taskData = {
      ...taskForm,
      durationMinutes,
      tags: typeof taskForm.tags === 'string' ? taskForm.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
      id: editingTask ? editingTask.id : generateId('t')
    };

    if (editingTask) {
      await updateEntity('taskTemplates', taskData);
    } else {
      await addEntity('taskTemplates', taskData);
    }
    resetForm();
  };

  const handleCopyTask = async (task) => {
    const copy = {
      ...task,
      id: generateId('t'),
      name: `${task.name} (Copy)`,
      created_at: new Date().toISOString()
    };
    await addEntity('taskTemplates', copy);
  };

  const resetForm = () => {
    setTaskForm({
      name: '', description: '', notes: '', assets: [],
      hours: 0, minutes: 0, priority: 'medium', tags: '',
      categoryId: '', isAppTask: false, appUrl: '', logoUrl: ''
    });
    setEditingTask(null);
    setEditorTab('general');
    setNewUrl('');
  };

  const startEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      ...task,
      hours: Math.floor((task.durationMinutes || 0) / 60),
      minutes: (task.durationMinutes || 0) % 60,
      tags: task.tags?.join(',') || ''
    });
    setNewUrl('');
  };

  return (
    <div className="h-full flex flex-col gap-6">
      <div className="flex-1 flex gap-6 overflow-hidden text-white">
        <div className="flex-1 bg-base-800 rounded-xl border border-base-700 p-6 flex flex-col overflow-hidden shadow-xl">
          {/* Unified Category Selector Header */}
          <div className="flex items-center justify-between mb-6 shrink-0">
            <h2 className="text-lg font-semibold shrink-0">Task Library</h2>
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide ml-4">
              <button 
                onClick={() => setActiveTab('all')} 
                className={`px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${activeTab === 'all' ? 'bg-primary text-white shadow-lg' : 'bg-base-900 text-gray-400 hover:text-white border border-base-700'}`}
              >
                All Tasks
              </button>
              {taskCategories.map(cat => (
                <div key={cat.id} className="flex items-center group shrink-0">
                  <button 
                    onClick={() => setActiveTab(cat.id)} 
                    className={`px-4 py-2 rounded-l-lg text-xs font-bold flex items-center gap-2 border border-r-0 transition-all whitespace-nowrap ${activeTab === cat.id ? 'bg-primary text-white border-primary shadow-lg' : 'bg-base-900 text-gray-400 hover:text-white border-base-700'}`}
                  >
                    <SafeIcon name={cat.icon || 'Layers'} className="w-3.5 h-3.5" /> {cat.name}
                  </button>
                  <button 
                    onClick={() => { setEditingCat(cat); setIsCatModalOpen(true); }}
                    className={`px-3 py-2 rounded-r-lg border transition-all ${activeTab === cat.id ? 'bg-primary text-white border-primary border-l-white/20' : 'bg-base-900 text-gray-500 hover:text-white border-base-700'}`}
                  >
                    <SafeIcon name="Edit2" className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button 
                onClick={() => { setEditingCat(null); setIsCatModalOpen(true); }}
                className="p-2 rounded-lg bg-base-900 border border-base-700 text-primary hover:bg-base-700 transition-colors shrink-0"
                title="Add Task Category"
              >
                <SafeIcon name="Plus" className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-auto space-y-3 pr-2 custom-scrollbar">
            {filteredTasks.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-600 opacity-50 italic">
                 <SafeIcon name="CheckSquare" className="w-12 h-12 mb-4" />
                 <p>No task templates in this category.</p>
              </div>
            ) : (
              filteredTasks.map(t => (
                <div key={t.id} className="bg-base-900/50 p-4 rounded-xl border border-base-700 flex justify-between items-center transition-all group hover:border-base-600">
                  <div className="flex gap-4 items-center min-w-0">
                    <a 
                      href={t.appUrl || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-lg bg-base-800 border border-base-700 flex items-center justify-center shrink-0 overflow-hidden shadow-inner transition-transform hover:scale-110 ${!t.appUrl && 'pointer-events-none'}`}
                    >
                      {t.logoUrl ? <img src={t.logoUrl} className="w-full h-full object-contain p-1" alt="" /> : <SafeIcon name="CheckSquare" className="w-5 h-5 text-gray-600" />}
                    </a>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-bold text-sm text-white truncate">{t.name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-md font-bold border uppercase tracking-tighter shrink-0 ${getPriorityColor(t.priority)}`}>{t.priority}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-gray-500 font-medium">
                        <div className="flex items-center gap-1 shrink-0"><SafeIcon name="Clock" className="w-3 h-3"/> {formatDuration(t.durationMinutes)}</div>
                        {t.categoryId && state.categories[t.categoryId] && (
                          <div className="flex items-center gap-1 text-primary/70 truncate">
                            <SafeIcon name={state.categories[t.categoryId].icon || 'Layers'} className="w-3 h-3" />
                            {state.categories[t.categoryId].name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Restored and Unified Action Controls */}
                  <div className="flex gap-1 shrink-0">
                    <button 
                      onClick={() => handleCopyTask(t)} 
                      className="p-2 text-gray-500 hover:text-accent hover:bg-accent/10 rounded-lg transition-all"
                      title="Duplicate Template"
                    >
                      <SafeIcon name="Copy" className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => startEditTask(t)} 
                      className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                      title="Edit Template"
                    >
                      <SafeIcon name="Edit2" className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => { if(confirm('Delete this template?')) deleteEntity('taskTemplates', t.id); }} 
                      className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all"
                      title="Delete Template"
                    >
                      <SafeIcon name="Trash2" className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="w-96 bg-base-800 rounded-xl border border-base-700 p-6 flex flex-col shrink-0 overflow-y-auto shadow-xl custom-scrollbar">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <SafeIcon name={editingTask ? "Edit3" : "PlusCircle"} className="text-primary" />
            {editingTask ? 'Edit Template' : 'Create Template'}
          </h2>
          
          <div className="flex gap-1 mb-6 bg-base-900 p-1 rounded-lg border border-base-700 shrink-0">
            {['general','content','assets'].map(tab => (
              <button key={tab} type="button" onClick={() => setEditorTab(tab)} className={`flex-1 py-2 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all ${editorTab === tab ? 'bg-primary text-white shadow-md' : 'text-gray-500 hover:text-gray-300'}`}>{tab}</button>
            ))}
          </div>

          <form onSubmit={handleTaskSubmit} className="space-y-4 flex-1">
            {editorTab === 'general' && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-2 duration-300">
                <Input label="Template Name" value={taskForm.name} onChange={e => setTaskForm({...taskForm, name: e.target.value})} required placeholder="e.g. Initial Research" />
                <Select 
                  label="Task Category" 
                  value={taskForm.categoryId} 
                  onChange={e => setTaskForm({...taskForm, categoryId: e.target.value})} 
                  options={[{label:'No Category', value:''}, ...taskCategories.map(c => ({label: c.name, value: c.id}))]} 
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input type="number" label="Hours" value={taskForm.hours} onChange={e => setTaskForm({...taskForm, hours: e.target.value})} min="0" />
                  <Input type="number" label="Minutes" value={taskForm.minutes} onChange={e => setTaskForm({...taskForm, minutes: e.target.value})} max="59" />
                </div>
                <Select label="Priority" value={taskForm.priority} onChange={e => setTaskForm({...taskForm, priority: e.target.value})} options={[{label:'Low',value:'low'},{label:'Medium',value:'medium'},{label:'High',value:'high'},{label:'Critical',value:'critical'}]} />
                
                <div className="pt-4 border-t border-base-700">
                  <label className="flex items-center gap-3 cursor-pointer group mb-4">
                    <div className="relative w-10 h-5 shrink-0">
                      <input type="checkbox" className="sr-only peer" checked={taskForm.isAppTask} onChange={e => setTaskForm({...taskForm, isAppTask: e.target.checked})} />
                      <div className="w-full h-full bg-base-700 rounded-full peer-checked:bg-primary transition-colors"></div>
                      <div className="absolute left-1 top-1 w-3 h-3 bg-white rounded-full transition-all peer-checked:left-6"></div>
                    </div>
                    <span className="text-xs font-semibold text-gray-300 group-hover:text-white">External App Association</span>
                  </label>
                  {taskForm.isAppTask && (
                    <div className="space-y-4 p-4 bg-base-900/50 rounded-xl border border-base-700 animate-in zoom-in-95 duration-200">
                      <Input label="App URL" value={taskForm.appUrl} onChange={e => setTaskForm({...taskForm, appUrl: e.target.value})} placeholder="https://github.com/..." />
                      <div className="space-y-4 pt-2 border-t border-base-700/50">
                        <ImageUpload label="Upload App Logo" currentUrl={taskForm.logoUrl} onUpload={(url) => setTaskForm({...taskForm, logoUrl: url})} />
                        <Input label="OR App Logo URL Link" value={taskForm.logoUrl} onChange={e => setTaskForm({...taskForm, logoUrl: e.target.value})} placeholder="https://..." />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {editorTab === 'content' && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-2 duration-300">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-gray-400">Description</label>
                  <textarea className="bg-base-900 border border-base-700 rounded-lg px-3 py-2 text-sm text-white min-h-[120px] resize-none focus:outline-none" value={taskForm.description} onChange={e => setTaskForm({...taskForm, description: e.target.value})} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-gray-400">Internal Team Notes</label>
                  <textarea className="bg-base-900 border border-base-700 rounded-lg px-3 py-2 text-sm text-gray-300 italic min-h-[80px] resize-none focus:outline-none" value={taskForm.notes} onChange={e => setTaskForm({...taskForm, notes: e.target.value})} />
                </div>
              </div>
            )}

            {editorTab === 'assets' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-2 duration-300">
                <ImageUpload label="Upload Local Asset" onUpload={(url) => {
                  setTaskForm(prev => ({...prev, assets: [...prev.assets, {type: 'file', url, name: 'Uploaded Asset'}]}));
                }} />
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-gray-400">Add External URL Asset</label>
                  <div className="flex gap-2">
                    <input className="flex-1 bg-base-900 border border-base-700 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary" placeholder="https://..." value={newUrl} onChange={e => setNewUrl(e.target.value)} />
                    <Button variant="secondary" type="button" onClick={() => {
                      if (!newUrl) return;
                      setTaskForm(prev => ({...prev, assets: [...prev.assets, {type: 'url', url: newUrl, name: 'External Link'}]}));
                      setNewUrl('');
                    }} disabled={!newUrl}>Add</Button>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-6 mt-auto border-t border-base-700 flex flex-col gap-2">
              <Button type="submit" className="w-full py-3">{editingTask ? 'Update Template' : 'Save Template'}</Button>
              {editingTask && <Button variant="ghost" className="w-full" onClick={resetForm}>Cancel Edit</Button>}
            </div>
          </form>
        </div>
      </div>
      <CategoryModal isOpen={isCatModalOpen} onClose={() => setIsCatModalOpen(false)} editingCategory={editingCat} categoryType="task" />
    </div>
  );
};